import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { useUserTimezone } from '@/shared/hooks';

import { ConsultationRecording } from '@/entities/transcriptor';

import { dentalConsultationMessages } from '../../config';
import { useRecordingAudio } from '../../hooks';
import { formatTimecode } from '../../lib';

import styles from './TranscriptionRecording.module.scss';

type TranscriptionRecordingProps = {
  recording: ConsultationRecording;
  index: number;
  className?: string;
  showHeader?: boolean;
};

export const TranscriptionRecording: FC<TranscriptionRecordingProps> = (
  props,
) => {
  const { recording, index, className, showHeader = false } = props;

  const { formatMessage, formatDate } = useIntl();
  const userTimezone = useUserTimezone();

  const { audioRef, src, seekTo, handleLoadedMetadata, handleError } =
    useRecordingAudio(recording);

  const hasValidDate = !Number.isNaN(recording.createdAt.getTime());

  const recordedAt = hasValidDate
    ? formatDate(recording.createdAt, {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: userTimezone,
      })
    : '';

  return (
    <section className={cn(styles.container, className)}>
      {showHeader && (
        <header className={styles.header}>
          <span className={cn('p2', styles.title)}>
            {formatMessage(dentalConsultationMessages.recordingTitle, {
              index: index + 1,
            })}
          </span>
          {hasValidDate && (
            <span className={cn('p3', styles.meta)}>{recordedAt}</span>
          )}
        </header>
      )}

      {src ? (
        <audio
          ref={audioRef}
          src={src}
          controls
          preload="metadata"
          className={styles.audioElement}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
        >
          {formatMessage(dentalConsultationMessages.audioNotSupported)}
        </audio>
      ) : (
        <p className={cn('p3', styles.processing)}>
          {formatMessage(dentalConsultationMessages.recordingProcessing)}
        </p>
      )}

      {recording.chunks.map((chunk, chunkIndex) => (
        <button
          key={`${chunkIndex}+${chunk.start}`}
          type="button"
          className={styles.transcriptItem}
          onClick={() => seekTo(chunk.start)}
        >
          <span className={styles.timestamp}>
            {formatTimecode(chunk.start)}
          </span>
          <span className={styles.transcriptText}>{chunk.text}</span>
        </button>
      ))}
    </section>
  );
};
